import { useEffect } from 'react';

export interface ClickOutsideProps {
  /**
   * Root node ID. If a click happens outside this node (and outside any ignoreIds),
   * it's treated as an outside click.
   */
  rootNodeId: string;
  /**
   * Ignore these id(s): if the click is inside any of these, do NOT trigger outside.
   */
  ignoreIds?: string | string[];
  /**
   * Outside click callback
   */
  onOutsideClick(event: Event): void;
}

/**
 * Simple outside-click listener. Renders nothing.
 * Attaches capture-phase listeners to match the old behavior.
 */
export default function ClickOutside({
  rootNodeId,
  ignoreIds,
  onOutsideClick,
}: ClickOutsideProps): null {
  useEffect(() => {
    // SSR guard
    if (typeof document === 'undefined' || !document.createElement) return;

    const handle = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Build the ignore list: root first (like original), then extras
      const ignores: string[] = [rootNodeId];
      if (ignoreIds) {
        ignores.push(...(Array.isArray(ignoreIds) ? ignoreIds : [ignoreIds]));
      }

      // If target is inside any ignored root/ids, DO NOT fire outside
      for (const id of ignores) {
        // Using closest keeps parity with the original logic:
        // it matches the element with id or any ancestor with that id.
        if (target.closest('#' + id)) return;
      }

      onOutsideClick(event);
    };

    // Use capture phase, like the original (third arg = true)
    document.addEventListener('mousedown', handle, true);
    document.addEventListener('touchstart', handle, true);

    return () => {
      document.removeEventListener('mousedown', handle, true);
      document.removeEventListener('touchstart', handle, true);
    };
  }, [rootNodeId, ignoreIds, onOutsideClick]);

  return null;
}
